import React, { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getState } from "../../api/api";
import {
  beautyDeleteSalonApi,
  beautyGetSalonApi,
  beautyListSalonsApi,
  beautyMeApi,
  beautySetSalonActivo,
  beautyToggleSalonApi,
} from "../../api/organizerBeautyApi";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { OrganizerHeaderContext } from "../Layout";
import useMultiSede from "../hooks/useMultiSede";
import AgendaTab from "./agenda/AgendaTab";
import ClientesTab from "../clientes/ClientesTab";
import TurnosGuidedFlow from "./primerUso/TurnosGuidedFlow";
import SalonCard from "./SalonCard";
import SalonForm from "./SalonForm";
import TuLinkDeReservas from "./TuLinkDeReservas";
import { ConfirmModal, OrgToast } from "./TurnosUi";
import { useVolverAPoliticas } from "../recordatorios/volverAPoliticas";
import "../reservas/reservas-organizer.css";

/* ─────────────────────────────────────────
   TurnosOrganizer — módulo de Turnos Beauty. Una sola ruta con estado local, como CanchasOrganizer.
   Un negocio tiene un salón: `GET /salon` devuelve `null` si todavía no lo configuró.
   La excepción son los negocios multi-sede (`multiSede` en el perfil): ven una card por salón y "Crear salón".
   El salón elegido se fija en el cliente de la API (beautySetSalonActivo), que lo manda como `salonId` en cada
   pedido del panel; los demás negocios nunca lo fijan y todo sale como siempre.
   Rutas: "list" (card del salón) | "agenda" | "clientes" | "form" (edición con Servicios y Profesionales).
───────────────────────────────────────── */

const BackArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </svg>
);

function EmptyPanel({ children }) {
  return (
    <div className="zr-panel" style={{ padding: 0 }}>
      <div className="zr-empty" style={{ flexDirection: "column", gap: 12, textAlign: "center", padding: 24 }}>
        {children}
      </div>
    </div>
  );
}

const TurnosOrganizer = () => {
  const { setPageHeader } = useContext(OrganizerHeaderContext);

  useEffect(() => {
    setPageHeader({
      title: "Turnos Beauty",
      subtitle: "Tu salón: servicios, profesionales, horarios y turnos.",
    });
  }, [setPageHeader]);

  // Un profesional vinculado (empleado con cuenta del salón) ve solo su agenda y su link.
  const esEmpleado = useSelector((state) => state.auth)?.user?.tipo === "empleado";
  const [profesional, setProfesional] = useState(null);
  const multiSede = useMultiSede();
  const [salon, setSalon] = useState(null);
  const [salones, setSalones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  // Primer uso: el negocio no tiene salón y eligió empezar la configuración.
  const [guiado, setGuiado] = useState(false);
  const [route, setRoute] = useState("list");
  // Profesional vinculado: pestaña de su vista ("agenda" | "clientes").
  const [tabProfesional, setTabProfesional] = useState("agenda");
  const [seccionInicial, setSeccionInicial] = useState("info");
  const [toast, setToast] = useState("");
  // Salón pendiente de confirmar su baja desde el menú de la card.
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [states, setStates] = useState([]);
  const cerrarToast = useCallback(() => setToast(""), []);

  const abrirForm = (seccion = "info") => {
    setSeccionInicial(seccion);
    setRoute("form");
  };

  // Coming back from Zoco Chat (templates): reopen this salon's form on Políticas.
  useVolverAPoliticas({ listo: !loading, locales: salon ? [salon] : [], abrir: () => abrirForm("politicas") });

  const loadSalon = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      // `/me` solo lo consulta el empleado; si falla se trata como "no vinculado" (comportamiento de siempre).
      const pedirMe = esEmpleado
        ? beautyMeApi().then((r) => r?.data?.info?.professional ?? r?.data?.professional ?? null).catch(() => null)
        : Promise.resolve(null);
      const [res, me] = await Promise.all([beautyGetSalonApi(), pedirMe]);
      setProfesional(me);
      setSalon(res?.data?.info || null);
    } catch (error) {
      setPageError(error?.response?.data?.message || "No pudimos cargar tu salón. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [esEmpleado]);

  useEffect(() => {
    loadSalon();
  }, [loadSalon]);

  // Multi-sede: el salón cargado es el activo del panel y la lista alimenta el selector.
  useEffect(() => {
    if (!multiSede) return undefined;
    beautySetSalonActivo(salon?._id || null);
    return undefined;
  }, [multiSede, salon?._id]);

  useEffect(() => {
    if (!multiSede) return undefined;
    let activo = true;
    (async () => {
      try {
        const res = await beautyListSalonsApi();
        if (activo) setSalones(Array.isArray(res?.data?.info) ? res.data.info : []);
      } catch (_) {
        if (activo) setSalones([]);
      }
    })();
    return () => {
      activo = false;
    };
  }, [multiSede, salon?._id, salon?.name]);

  useEffect(() => {
    if (!multiSede) return undefined;
    return () => beautySetSalonActivo(null);
  }, [multiSede]);

  // Provincias para la ubicación del asistente; si falla, el asistente muestra la lista vacía.
  useEffect(() => {
    (async () => {
      try {
        const res = await getState();
        setStates(Array.isArray(res?.data?.info) ? res.data.info : []);
      } catch (_) { }
    })();
  }, []);

  // Tras guardar el salón se refresca lo que muestra el módulo (nombre, modo de reserva).
  const handleSalonGuardado = (actualizado) => {
    if (actualizado) setSalon((prev) => ({ ...prev, ...actualizado }));
    else loadSalon();
  };

  // Servicios/Profesionales re-derivan el modo de reserva (seña) y avisan para mantener el salón al día.
  const handleSalonCambio = (patch) => setSalon((prev) => ({ ...prev, ...patch }));

  // Asistente terminado: se recarga el salón; si quedó un paso opcional pendiente se abre esa sección del formulario.
  const handleTerminarGuiado = async (ctx, seccion) => {
    setGuiado(false);
    if (seccion) abrirForm(seccion);
    else setRoute("list");
    await loadSalon();
  };

  // Si se sale después de crear el salón, se muestra en lugar de la invitación vacía.
  const handleCancelarGuiado = async (salonCreado) => {
    setGuiado(false);
    if (salonCreado) await loadSalon();
  };

  // Acción desde la card de otro salón: se fija como activo y se recarga antes de abrir la sección, para que todo
  // lo que se monte después pida el salón elegido.
  const enSalon = (accion) => async (s) => {
    if (s?._id && s._id !== salon?._id) {
      beautySetSalonActivo(s._id);
      await loadSalon();
    }
    accion();
  };

  const handleToggleStatus = async (s) => {
    try {
      const res = await beautyToggleSalonApi(s._id);
      const habilitado = res?.data?.info?.isEnabled;
      const conEstado = (x) => ({ ...x, isEnabled: typeof habilitado === "boolean" ? habilitado : x.isEnabled === false });
      setSalones((prev) => prev.map((x) => (x._id === s._id ? conEstado(x) : x)));
      if (s._id === salon?._id) setSalon(conEstado);
      setToast("Estado del salón actualizado.");
    } catch (e) {
      setToast(e?.response?.data?.message || "No pudimos cambiar el estado del salón. Probá de nuevo.");
    }
  };

  // Baja del salón (POST /salon/delete, baja lógica en el back). El back responde 409 con el motivo si el salón
  // todavía tiene turnos por venir; ese mensaje se muestra tal cual. Si se borra el salón activo se suelta la
  // selección y se recarga: el back devuelve el siguiente salón del negocio, o null (el negocio puede crear otro).
  const confirmarEliminar = async () => {
    const s = aEliminar;
    setEliminando(true);
    try {
      await beautyDeleteSalonApi(s._id);
      setAEliminar(null);
      setToast("Salón eliminado.");
      setSalones((prev) => prev.filter((x) => x._id !== s._id));
      if (s._id === salon?._id) {
        if (multiSede) beautySetSalonActivo(null);
        await loadSalon();
      }
    } catch (e) {
      setAEliminar(null);
      setToast(e?.response?.data?.message || "No pudimos eliminar el salón. Probá de nuevo.");
    } finally {
      setEliminando(false);
    }
  };

  const handleCopiarEnlace = async (s) => {
    const url = `${window.location.origin}/beauty/${s.slug || s._id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast("Enlace copiado al portapapeles.");
    } catch (_) {
      setToast("No se pudo copiar el enlace.");
    }
  };

  if (loading) return <SpinLoading />;

  if (pageError) {
    return (
      <div className="zr-app zr-main turnos-beauty">
        <div className="org-page-error">
          <p>{pageError}</p>
          <button type="button" onClick={loadSalon}>Reintentar</button>
        </div>
      </div>
    );
  }

  if (!salon || guiado) {
    return (
      <div className="zr-app zr-main turnos-beauty">
        {guiado ? (
          <TurnosGuidedFlow
            states={states}
            onTerminar={handleTerminarGuiado}
            onCancelar={handleCancelarGuiado}
            onSalonCreado={multiSede ? beautySetSalonActivo : undefined}
          />
        ) : (
          <EmptyPanel>
            <p style={{ margin: 0 }}>Este negocio todavía no tiene salón.</p>
            <p style={{ margin: 0 }}>Configuralo para cargar tus servicios, tu equipo y tus horarios, y empezar a recibir turnos.</p>
            <button type="button" className="zr-btn zr-btn--dark" onClick={() => setGuiado(true)}>
              Empezar configuración
            </button>
          </EmptyPanel>
        )}
        {/* El aviso de baja del último salón llega a esta vista. */}
        <OrgToast message={toast} onClose={cerrarToast} />
      </div>
    );
  }

  if (profesional) {
    return (
      <div className="zr-app zr-main turnos-beauty">
        <div className="zr-subhead" style={{ marginBottom: 16 }}>
          <div>
            <h2 className="zr-subhead__title">Mi agenda</h2>
            <p className="zr-subhead__hint">{salon.name}</p>
          </div>
        </div>
        <TuLinkDeReservas professional={profesional} salon={salon} />
        <nav className="zr-tabs" aria-label="Secciones">
          {[["agenda", "AGENDA"], ["clientes", "CLIENTES"]].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={"zr-tabs__item" + (tabProfesional === id ? " zr-tabs__item--active" : "")}
              aria-current={tabProfesional === id ? "page" : undefined}
              onClick={() => setTabProfesional(id)}
            >
              {label}
            </button>
          ))}
        </nav>
        <div style={{ paddingBottom: 60 }}>
          {tabProfesional === "agenda" ? <AgendaTab salon={salon} /> : <ClientesTab />}
        </div>
      </div>
    );
  }

  return (
    <div className="zr-app zr-main turnos-beauty">
      {route === "list" && multiSede && salones.length > 0 && (
        <div className="zr-subhead" style={{ marginBottom: 16 }}>
          <button type="button" className="zr-btn zr-btn--dark" onClick={() => setGuiado(true)}>
            Crear salón
          </button>
        </div>
      )}

      {route === "list" && (
        <SalonCard
          salon={salon}
          salones={multiSede ? salones.map((x) => (x._id === salon._id ? { ...x, ...salon } : x)) : undefined}
          states={states}
          onVerAgenda={enSalon(() => setRoute("agenda"))}
          onVerClientes={enSalon(() => setRoute("clientes"))}
          onVerProfesionales={enSalon(() => abrirForm("profesionales"))}
          onEditar={enSalon(() => abrirForm("info"))}
          onToggleStatus={handleToggleStatus}
          onCopiarEnlace={handleCopiarEnlace}
          onEliminar={setAEliminar}
        />
      )}

      {aEliminar && (
        <ConfirmModal
          title="¿Eliminar salón?"
          busy={eliminando}
          onConfirm={confirmarEliminar}
          onCancel={() => setAEliminar(null)}
        >
          Se eliminará <strong>{aEliminar.name}</strong> y dejará de recibir turnos. Esta acción no se puede deshacer.
        </ConfirmModal>
      )}

      {route === "agenda" && (
        <div style={{ paddingBottom: 60 }}>
          <div className="zr-backbar">
            <button type="button" className="zr-btn zr-btn--soft" onClick={() => setRoute("list")}>
              <BackArrow />
              Volver
            </button>
            <div className="zr-subhead">
              <h2 className="zr-subhead__title">Agenda de {salon.name}</h2>
              <p className="zr-subhead__hint">| Los turnos del día, por profesional.</p>
            </div>
          </div>
          <AgendaTab salon={salon} onIrAProfesionales={() => abrirForm("profesionales")} />
        </div>
      )}

      {route === "clientes" && (
        <div style={{ paddingBottom: 60 }}>
          <ClientesTab
            barra={(fichaAbierta, cerrarFicha) => (
              <div className="zr-backbar">
                <button
                  type="button"
                  className="zr-btn zr-btn--soft"
                  onClick={fichaAbierta ? cerrarFicha : () => setRoute("list")}
                >
                  <BackArrow />
                  {fichaAbierta ? "Volver a clientes" : "Volver"}
                </button>
                <div className="zr-subhead">
                  <h2 className="zr-subhead__title">Clientes de {salon.name}</h2>
                  <p className="zr-subhead__hint">Fichas, notas y el historial de cada cliente.</p>
                </div>
              </div>
            )}
          />
        </div>
      )}

      {route === "form" && (
        <SalonForm
          key={salon._id}
          salon={salon}
          states={states}
          initialSection={seccionInicial}
          onSaved={handleSalonGuardado}
          onSalonChange={handleSalonCambio}
          onVolver={() => setRoute("list")}
        />
      )}

      <OrgToast message={toast} onClose={cerrarToast} />
    </div>
  );
};

export default TurnosOrganizer;
